import { useState, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import {
  User, Mail, Phone, MessageCircle, Send, Gamepad2,
  Wrench, Megaphone, ChevronRight, AlertCircle, CheckCircle2,
} from "lucide-react";

const platformRoles = [
  {
    key: "player" as const,
    icon: Gamepad2,
    label: "Player",
    desc: "Compete in skill-based tournaments and earn real money.",
    contactRequired: false,
  },
  {
    key: "creator" as const,
    icon: Wrench,
    label: "Creator",
    desc: "Design and host games. AI helps you build viral competitions.",
    contactRequired: true,
  },
  {
    key: "promoter" as const,
    icon: Megaphone,
    label: "Promoter",
    desc: "Monetize your audience by promoting tournaments for commission.",
    contactRequired: true,
  },
];

export default function ProfileSetup() {
  const { user, loading: authLoading } = useAuth();
  const [, navigate] = useLocation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  // Pre-select role from sessionStorage if the user chose one during signup
  const storedRole = sessionStorage.getItem("gp.signupRole") as "player" | "creator" | "promoter" | null;
  const [selectedRole, setSelectedRole] = useState<"player" | "creator" | "promoter">(
    storedRole ?? "player"
  );
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [telegram, setTelegram] = useState("");
  const [error, setError] = useState("");
  // Skip straight to step 2 (role confirmation) when a role was already chosen at signup
  const [step, setStep] = useState(storedRole ? 2 : 1); // 1 = basics, 2 = role, 3 = contact

  const utils = trpc.useUtils();
  const setupMutation = trpc.profile.setupProfile.useMutation({
    onSuccess: async () => {
      // Clear signup role from sessionStorage now that setup is done
      sessionStorage.removeItem("gp.signupRole");
      sessionStorage.removeItem("gp.signupEmail");
      // Invalidate auth.me so dashboard picks up profileComplete=1
      await utils.auth.me.invalidate();
      navigate("/console");
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  // Pre-fill from existing user data
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Please log in to set up your profile.</p>
          <Button onClick={() => navigate("/")} className="bg-neon hover:bg-neon-dim text-background font-bold">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const roleConfig = platformRoles.find((r) => r.key === selectedRole);
  const needsContact = roleConfig?.contactRequired ?? false;
  const hasContact = !!(phone.trim() || whatsapp.trim() || telegram.trim());

  const canSubmit = name.trim() && email.trim() && email.includes("@") &&
    (!needsContact || hasContact);

  const handleSubmit = () => {
    setError("");
    if (!name.trim()) { setError("Name is required."); return; }
    if (!email.trim() || !email.includes("@")) { setError("A valid email is required."); return; }
    if (needsContact && !hasContact) {
      setError("Creators and promoters must provide at least one contact method (phone, WhatsApp, or Telegram).");
      return;
    }

    setupMutation.mutate({
      name: name.trim(),
      email: email.trim(),
      platformRole: selectedRole,
      phone: phone.trim() || null,
      whatsapp: whatsapp.trim() || null,
      telegram: telegram.trim() || null,
    });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      {/* Background decorations */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-neon/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-accent/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center gap-2.5 justify-center mb-8">
          <div className="w-10 h-10 rounded-lg bg-neon/15 border border-neon/30 flex items-center justify-center">
            <span className="text-neon font-extrabold text-base tracking-tighter">GP</span>
          </div>
          <span className="text-2xl font-extrabold tracking-tight uppercase">
            <span className="text-white">Game</span>
            <span className="text-neon">Point</span>
          </span>
        </div>

        {/* Progress steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                  step >= s
                    ? "bg-neon text-background"
                    : "bg-navy-light text-muted-foreground border border-border"
                }`}
              >
                {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-0.5 ${step > s ? "bg-neon" : "bg-border"}`} />
              )}
            </div>
          ))}
        </div>

        <div className="glass-panel rounded-2xl p-8">
          {/* Step 1: Basics */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-extrabold text-foreground uppercase mb-2">
                  Welcome to GamePoint
                </h1>
                <p className="text-sm text-muted-foreground">
                  Let's set up your profile. This takes less than a minute.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-2">
                    <User className="w-4 h-4 text-neon" />
                    Display Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your display name"
                    className="w-full bg-navy-light border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-neon/50 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-neon" />
                    Email Address <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full bg-navy-light border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-neon/50 transition-colors"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Required for account notifications and prize payouts.
                  </p>
                </div>
              </div>

              <Button
                onClick={() => {
                  if (!name.trim() || !email.trim() || !email.includes("@")) {
                    setError("Please fill in your name and a valid email.");
                    return;
                  }
                  setError("");
                  setStep(2);
                }}
                disabled={!name.trim() || !email.trim()}
                className="w-full bg-neon hover:bg-neon-dim text-background font-extrabold uppercase tracking-wide py-6"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Step 2: Platform Role */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-extrabold text-foreground uppercase mb-2">
                  Choose Your Role
                </h1>
                <p className="text-sm text-muted-foreground">
                  How do you want to use GamePoint? You can change this later.
                </p>
              </div>

              <div className="space-y-3">
                {platformRoles.map((role) => (
                  <button
                    key={role.key}
                    onClick={() => setSelectedRole(role.key)}
                    className={`w-full text-left p-4 rounded-xl border transition-all ${
                      selectedRole === role.key
                        ? "border-neon bg-neon/10"
                        : "border-border bg-navy-light hover:border-neon/30"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedRole === role.key ? "bg-neon/20" : "bg-navy-lighter"
                      }`}>
                        <role.icon className={`w-5 h-5 ${
                          selectedRole === role.key ? "text-neon" : "text-muted-foreground"
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${
                            selectedRole === role.key ? "text-neon" : "text-foreground"
                          }`}>
                            {role.label}
                          </span>
                          {role.contactRequired && (
                            <span className="text-[10px] bg-gold/20 text-gold px-1.5 py-0.5 rounded font-medium">
                              Contact Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">{role.desc}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedRole === role.key ? "border-neon" : "border-border"
                      }`}>
                        {selectedRole === role.key && (
                          <div className="w-2.5 h-2.5 rounded-full bg-neon" />
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1 border-border text-foreground"
                >
                  Back
                </Button>
                <Button
                  onClick={() => {
                    setError("");
                    setStep(3);
                  }}
                  className="flex-1 bg-neon hover:bg-neon-dim text-background font-extrabold uppercase tracking-wide"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Contact Info */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <h1 className="text-2xl font-extrabold text-foreground uppercase mb-2">
                  Contact Info
                </h1>
                <p className="text-sm text-muted-foreground">
                  {needsContact
                    ? "As a " + selectedRole + ", you need at least one contact method for game management notifications."
                    : "Optional — add contact info so we can reach you about tournaments and prizes."}
                </p>
              </div>

              {needsContact && (
                <div className="flex items-start gap-2 bg-gold/10 border border-gold/30 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <p className="text-xs text-gold">
                    Creators and promoters must provide at least one of: phone number, WhatsApp, or Telegram.
                    This ensures you receive real-time notifications for game management.
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-neon" />
                    Phone Number
                    {needsContact && !whatsapp.trim() && !telegram.trim() && (
                      <span className="text-destructive text-xs">(at least one required)</span>
                    )}
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="w-full bg-navy-light border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-neon/50 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-neon" />
                    WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="w-full bg-navy-light border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-neon/50 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 flex items-center gap-2">
                    <Send className="w-4 h-4 text-neon" />
                    Telegram
                  </label>
                  <input
                    type="text"
                    value={telegram}
                    onChange={(e) => setTelegram(e.target.value)}
                    placeholder="@yourusername"
                    className="w-full bg-navy-light border border-border rounded-lg px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-neon/50 transition-colors"
                  />
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
                  <p className="text-xs text-destructive">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="flex-1 border-border text-foreground"
                >
                  Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!canSubmit || setupMutation.isPending}
                  className="flex-1 bg-neon hover:bg-neon-dim text-background font-extrabold uppercase tracking-wide"
                >
                  {setupMutation.isPending ? (
                    <div className="w-5 h-5 border-2 border-background border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>Complete Setup</>
                  )}
                </Button>
              </div>

              {!needsContact && (
                <button
                  onClick={handleSubmit}
                  disabled={setupMutation.isPending}
                  className="w-full text-center text-sm text-muted-foreground hover:text-neon transition-colors"
                >
                  Skip for now →
                </button>
              )}
            </div>
          )}
        </div>

        {/* Data ownership notice */}
        <p className="text-center text-xs text-muted-foreground mt-6 max-w-sm mx-auto">
          Your data belongs to you. We only use contact info for game notifications.
          You can review and update your information at any time in Settings.
        </p>
      </div>
    </div>
  );
}
